import { useRef, useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGhost, btnGold, errMsg, inp, lbl } from "@/components/attendance/ui";

export default function ImportPanel({ endpoint, testId, title, help, withLanguage, onDone }) {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("en");
  const [mode, setMode] = useState("skip");
  const [publish, setPublish] = useState(true);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const run = async (dry) => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dry ? "true" : "false");
    fd.append("mode", mode);
    if (withLanguage) { fd.append("language", language); fd.append("publish", publish ? "true" : "false"); }
    try {
      const r = await api.post(endpoint, fd);
      setReport(r.data);
      if (!dry) { toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`); onDone?.(); }
    } catch (e) { toast.error(errMsg(e, "Import failed")); } finally { setBusy(false); }
  };

  return (
    <section className="luxury-card rounded-xl p-5 space-y-4" data-testid={testId}>
      <h3 className="font-serif-display text-2xl text-white">{title}</h3>
      <p className="text-xs text-neutral-400 leading-relaxed">{help}</p>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className={lbl}>File</label>
          <input ref={ref} type="file" accept=".csv,.xlsx,.json,.txt" onChange={(e) => { setFile(e.target.files[0] || null); setReport(null); }} data-testid={`${testId}-file`} className="text-xs text-neutral-300" />
        </div>
        {withLanguage && (
          <div>
            <label className={lbl}>Language</label>
            <select className={inp} value={language} onChange={(e) => setLanguage(e.target.value)} data-testid={`${testId}-language`}><option value="en">English</option><option value="yo">Yoruba</option></select>
          </div>
        )}
        <div>
          <label className={lbl}>Existing numbers</label>
          <select className={inp} value={mode} onChange={(e) => setMode(e.target.value)} data-testid={`${testId}-mode`}><option value="skip">Skip (keep current)</option><option value="update">Update from file</option></select>
        </div>
      </div>
      {withLanguage && <label className="text-xs text-neutral-300 inline-flex items-center gap-2"><input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-[#D4AF37]" data-testid={`${testId}-publish`} /> Publish new hymns immediately</label>}
      <div className="flex gap-2">
        <button onClick={() => run(true)} disabled={busy} className={btnGhost} data-testid={`${testId}-preview`}>{busy && <Loader2 size={13} className="animate-spin" />} Check file</button>
        <button onClick={() => run(false)} disabled={busy || !report?.dry_run} className={btnGold} data-testid={`${testId}-commit`}><FileUp size={14} /> Import</button>
      </div>
      {report && <Report r={report} testId={testId} />}
    </section>
  );
}

function Report({ r, testId }) {
  return (
    <div className="text-sm space-y-2" data-testid={`${testId}-report`}>
      <div className="text-neutral-300">{r.dry_run ? "Check result" : "Imported"}: {r.rows} rows · <span className="text-emerald-300">{r.created} new</span> · {r.updated} updated · {r.skipped} skipped{r.linked != null && ` · ${r.linked} linked`} · <span className={r.errors.length ? "text-amber-300" : ""}>{r.errors.length} errors</span></div>
      {r.preview?.map((p) => (
        <div key={p.cac_number} className="text-xs text-neutral-400 border-l border-white/10 pl-3"><span className="text-[#D4AF37]">CAC {p.cac_number}</span> {p.title} · {p.verses} blocks<div className="whitespace-pre-line text-neutral-500 mt-1">{p.first_verse}</div></div>
      ))}
      {r.errors.slice(0, 20).map((e) => <div key={e.row} className="text-xs text-amber-300">Row {e.row}: {e.error}</div>)}
    </div>
  );
}
